import React, { useCallback, useEffect, useState, useMemo } from "react";
import { Plus, Edit, Trash2, HelpCircle, CheckCircle2, XCircle, Search, Loader2, ArrowUp, ArrowDown } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { WorkbenchError, WorkbenchHeader } from "@/components/admin/AdminWorkbenchUi";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

export default function AdminFaqs() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("ALL");
  const [editingFaq, setEditingFaq] = useState(null); // null, {} for new, or faq obj for edit
  const [deleteId, setDeleteId] = useState(null);
  const [error, setError] = useState("");

  const loadFaqs = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/v1/admin/faqs");
      const items = res?.data?.items || res?.data?.data || res?.data || [];
      setFaqs(Array.isArray(items) ? items : []);
    } catch (err) {
      setFaqs([]);
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFaqs();
  }, [loadFaqs]);

  const categories = useMemo(() => {
    const set = new Set(faqs.map((f) => f.category).filter(Boolean));
    return ["ALL", ...Array.from(set)];
  }, [faqs]);

  const filteredFaqs = useMemo(() => {
    return faqs.filter((f) => {
      const matchesCat = selectedCategory === "ALL" || f.category === selectedCategory;
      const q = query.toLowerCase().trim();
      const matchesQuery =
        !q ||
        (f.question && f.question.toLowerCase().includes(q)) ||
        (f.answer && f.answer.toLowerCase().includes(q)) ||
        (f.category && f.category.toLowerCase().includes(q));
      return matchesCat && matchesQuery;
    });
  }, [faqs, selectedCategory, query]);

  const handleSaveFaq = async (e) => {
    e.preventDefault();
    if (!editingFaq) return;
    setBusy(true);
    try {
      if (editingFaq.id) {
        await api.put(`/v1/admin/faqs/${editingFaq.id}`, editingFaq);
        toast.success("FAQ updated successfully");
      } else {
        await api.post("/v1/admin/faqs", editingFaq);
        toast.success("FAQ created successfully");
      }
      setEditingFaq(null);
      await loadFaqs();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteFaq = async () => {
    if (!deleteId) return;
    setBusy(true);
    try {
      await api.delete(`/v1/admin/faqs/${deleteId}`);
      toast.success("FAQ deleted successfully");
      setDeleteId(null);
      await loadFaqs();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleToggleActive = async (faq) => {
    setBusy(true);
    try {
      const updatedActive = !faq.active;
      await api.put(`/v1/admin/faqs/${faq.id}`, { active: updatedActive });
      toast.success(`FAQ ${updatedActive ? "activated" : "deactivated"}`);
      setFaqs((prev) =>
        prev.map((item) => (item.id === faq.id ? { ...item, active: updatedActive } : item))
      );
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleMoveFaq = async (idx, direction) => {
    const targetIdx = idx + direction;
    if (targetIdx < 0 || targetIdx >= filteredFaqs.length) return;
    const currentFaq = filteredFaqs[idx];
    const targetFaq = filteredFaqs[targetIdx];

    const currentOrder = currentFaq.sort_order ?? ((idx + 1) * 10);
    const targetOrder = targetFaq.sort_order ?? ((targetIdx + 1) * 10);

    try {
      setBusy(true);
      await api.post("/v1/admin/faqs/reorder", { items: [
        { id: currentFaq.id, sort_order: targetOrder },
        { id: targetFaq.id, sort_order: currentOrder },
      ] });
      toast.success("FAQ reordered successfully");
      await loadFaqs();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-faqs-page">
      <WorkbenchHeader
        eyebrow="Content management"
        title="FAQ Management"
        description="Manage database-driven Frequently Asked Questions shown on the homepage, footer and FAQ page."
        actions={<Button
          onClick={() =>
            setEditingFaq({
              category: "General",
              question: "",
              answer: "",
              sort_order: (faqs.length + 1) * 10,
              active: true,
            })
          }
          className="gap-2 font-bold text-xs"
          data-testid="create-faq-btn"
        >
          <Plus className="w-4 h-4" aria-hidden="true" /> Add New FAQ
        </Button>}
      />

      {/* Filter and Search Bar */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between bg-card border border-border/60 p-4 rounded-2xl shadow-sm">
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search question, answer, category..."
            maxLength={200}
            className="pl-9 bg-background text-xs"
          />
        </div>

        <div className="flex flex-wrap gap-1.5 w-full sm:w-auto">
          {categories.map((cat) => (
            <button
              type="button"
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              aria-pressed={selectedCategory === cat}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                selectedCategory === cat
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* FAQs List Table / Cards */}
      {error ? (
        <WorkbenchError message={error} onRetry={loadFaqs} />
      ) : loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground text-sm gap-2">
          <Loader2 className="w-5 h-5 animate-spin text-primary" /> Loading FAQs from Database…
        </div>
      ) : filteredFaqs.length === 0 ? (
        <div className="text-center py-20 bg-card border border-border/40 rounded-2xl p-6 text-muted-foreground space-y-2">
          <HelpCircle className="w-10 h-10 mx-auto text-muted-foreground/60" />
          <p className="font-semibold text-sm">No FAQs found matching filter criteria.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {filteredFaqs.map((faq, index) => (
            <div
              key={faq.id}
              className={`p-4 bg-card border rounded-2xl shadow-sm transition flex flex-col md:flex-row md:items-center justify-between gap-4 ${
                faq.active ? "border-border/60 hover:border-primary/40" : "border-border/40 opacity-60 bg-muted/20"
              }`}
              data-testid={`faq-card-${faq.id}`}
            >
              <div className="space-y-1 flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-primary/10 text-primary border border-primary/20">
                    {faq.category}
                  </span>
                  <span className="text-[10px] font-mono text-muted-foreground">Order: {faq.sort_order}</span>
                  <span
                    className={`text-[10px] font-bold px-2 py-0.5 rounded flex items-center gap-1 ${
                      faq.active
                        ? "bg-emerald-500/10 text-emerald-600 border border-emerald-500/20"
                        : "bg-muted text-muted-foreground border border-border"
                    }`}
                  >
                    {faq.active ? (
                      <>
                        <CheckCircle2 className="w-3 h-3 text-emerald-600" /> Active
                      </>
                    ) : (
                      <>
                        <XCircle className="w-3 h-3 text-muted-foreground" /> Inactive
                      </>
                    )}
                  </span>
                </div>
                <h3 className="font-bold text-sm text-foreground pt-1">{faq.question}</h3>
                <p className="text-xs text-muted-foreground line-clamp-2">{faq.answer}</p>
              </div>

              <div className="flex items-center gap-1.5 shrink-0 self-end md:self-center">
                <div className="flex items-center gap-1 bg-muted/30 p-1 rounded-xl border border-border/40">
                  <Button
                    size="icon"
                    variant="ghost"
                    disabled={index === 0 || busy}
                    onClick={() => handleMoveFaq(index, -1)}
                    className="w-7 h-7"
                    title="Move Up"
                    aria-label={`Move ${faq.question || "FAQ"} up`}
                  >
                    <ArrowUp className="w-3.5 h-3.5 text-muted-foreground" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    disabled={index === filteredFaqs.length - 1 || busy}
                    onClick={() => handleMoveFaq(index, 1)}
                    className="w-7 h-7"
                    title="Move Down"
                    aria-label={`Move ${faq.question || "FAQ"} down`}
                  >
                    <ArrowDown className="w-3.5 h-3.5 text-muted-foreground" />
                  </Button>
                </div>

                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleToggleActive(faq)}
                  disabled={busy}
                  className="text-xs"
                >
                  {faq.active ? "Deactivate" : "Activate"}
                </Button>

                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setEditingFaq({ ...faq })}
                  disabled={busy}
                  className="gap-1 text-xs"
                  data-testid={`edit-faq-${faq.id}`}
                >
                  <Edit className="w-3.5 h-3.5" /> Edit
                </Button>

                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setDeleteId(faq.id)}
                  disabled={busy}
                  className="text-destructive hover:bg-destructive/10 border-destructive/40 text-xs"
                  aria-label={`Delete FAQ: ${faq.question || "Untitled"}`}
                  data-testid={`delete-faq-${faq.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={Boolean(editingFaq)} onOpenChange={(open) => { if (!open && !busy) setEditingFaq(null); }}>
        {editingFaq && <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto rounded-2xl border-border bg-card p-5 text-foreground sm:p-6">
          <DialogHeader><DialogTitle>{editingFaq.id ? "Edit FAQ Item" : "Create New FAQ Item"}</DialogTitle></DialogHeader>
          <form
            onSubmit={handleSaveFaq}
            className="space-y-4"
          >
            <div className="space-y-4">
              <div className="grid gap-1.5">
                <Label htmlFor="faq-category">Category</Label>
                <input
                  id="faq-category"
                  list="category-suggestions"
                  value={editingFaq.category || ""}
                  onChange={(e) => setEditingFaq({ ...editingFaq, category: e.target.value })}
                  placeholder="e.g. General, For Buyers, For Agents & Offices, Account & Security"
                  maxLength={120}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  required
                />
                <datalist id="category-suggestions">
                  <option value="General" />
                  <option value="For Buyers" />
                  <option value="For Agents & Offices" />
                  <option value="Account & Security" />
                </datalist>
              </div>

              <div className="grid gap-1.5">
                <Label htmlFor="faq-question">Question</Label>
                <Input
                  id="faq-question"
                  value={editingFaq.question || ""}
                  onChange={(e) => setEditingFaq({ ...editingFaq, question: e.target.value })}
                  placeholder="Enter question text..."
                  maxLength={500}
                  required
                />
              </div>

              <div className="grid gap-1.5">
                <Label htmlFor="faq-answer">Answer</Label>
                <textarea
                  id="faq-answer"
                  value={editingFaq.answer || ""}
                  onChange={(e) => setEditingFaq({ ...editingFaq, answer: e.target.value })}
                  placeholder="Enter detailed answer text..."
                  maxLength={10000}
                  rows={4}
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-1.5">
                  <Label htmlFor="faq-sort">Sort Order</Label>
                  <Input
                    id="faq-sort"
                    type="number"
                    min="0"
                    value={editingFaq.sort_order ?? 0}
                    onChange={(e) => setEditingFaq({ ...editingFaq, sort_order: Math.max(0, parseInt(e.target.value) || 0) })}
                  />
                </div>

                <div className="grid gap-1.5">
                  <Label htmlFor="faq-active">Status</Label>
                  <select
                    id="faq-active"
                    value={editingFaq.active ? "true" : "false"}
                    onChange={(e) => setEditingFaq({ ...editingFaq, active: e.target.value === "true" })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="true">Active (Visible)</option>
                    <option value="false">Inactive (Hidden)</option>
                  </select>
                </div>
              </div>
            </div>

            <DialogFooter className="border-t border-border/60 pt-4 mt-6">
              <Button type="button" variant="outline" onClick={() => setEditingFaq(null)} disabled={busy}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy} className="font-bold">
                {busy ? "Saving..." : editingFaq.id ? "Save Changes" : "Create FAQ"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>}
      </Dialog>

      <AlertDialog open={Boolean(deleteId)} onOpenChange={(open) => { if (!open && !busy) setDeleteId(null); }}>
        <AlertDialogContent className="rounded-2xl bg-card text-foreground">
          <AlertDialogHeader><AlertDialogTitle className="text-destructive">Confirm FAQ Deletion</AlertDialogTitle><AlertDialogDescription>Are you sure you want to permanently delete this FAQ item from the database?</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel><AlertDialogAction onClick={(event) => { event.preventDefault(); handleDeleteFaq(); }} disabled={busy} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">{busy ? "Deleting..." : "Yes, Delete FAQ"}</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
